/**
 * TechnicianProjects Page
 *
 * Mobile-first project list for technician portal.
 * Shows projects where the technician has assigned tasks.
 * Features filtering, search, pull-to-refresh, and glassmorphism styling.
 *
 * @module pages/technician/TechnicianProjects
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  Container,
  Card,
  Row,
  Col,
  Form,
  Badge,
  Button,
  Spinner,
  Alert,
  InputGroup,
  Collapse,
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { getAssignedProjects } from '../../services/technicianProjectService';
import { unwrapServiceResult } from '../../utils/serviceResult';
import { PROJECT_STATUS, CACHE_TIME } from '../../utils/constants';
import './TechnicianPages.css';

/**
 * Status badge variant mapping
 */
const STATUS_VARIANTS = {
  [PROJECT_STATUS.QUOTATION_SENT]: 'warning',
  [PROJECT_STATUS.QUOTATION_ACCEPTED]: 'info',
  [PROJECT_STATUS.PO_RECEIVED]: 'primary',
  [PROJECT_STATUS.IN_PROGRESS]: 'success',
  [PROJECT_STATUS.CLOSED]: 'dark',
};

/**
 * Status labels in Spanish
 */
const STATUS_LABELS = {
  [PROJECT_STATUS.QUOTATION_SENT]: 'Cotización Enviada',
  [PROJECT_STATUS.QUOTATION_ACCEPTED]: 'Cotización Aceptada',
  [PROJECT_STATUS.PO_RECEIVED]: 'OC Recibida',
  [PROJECT_STATUS.IN_PROGRESS]: 'Activo',
  [PROJECT_STATUS.CLOSED]: 'Cerrado',
};

/**
 * Filter options for status dropdown
 */
const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: PROJECT_STATUS.IN_PROGRESS, label: 'Activo' },
  { value: PROJECT_STATUS.PO_RECEIVED, label: 'OC Recibida' },
  { value: PROJECT_STATUS.CLOSED, label: 'Cerrado' },
];

/**
 * Project Card Component
 * Displays individual project information with glassmorphism styling
 */
const ProjectCard = ({ project, onClick }) => {
  const {
    project_number,
    title,
    client,
    status,
    taskSummary,
    created_at,
  } = project;

  /**
   * Format date for display
   */
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-CR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <Card
      className="dashboard-card mb-3"
      onClick={onClick}
      style={{ cursor: 'pointer' }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
    >
      <Card.Body>
        {/* Header: Project number and status */}
        <div className="d-flex justify-content-between align-items-start mb-2">
          <span className="text-muted" style={{ fontSize: '0.8rem' }}>
            {project_number || 'Sin numero'}
          </span>
          <Badge bg={STATUS_VARIANTS[status] || 'secondary'}>
            {STATUS_LABELS[status] || status}
          </Badge>
        </div>

        {/* Title */}
        <h5 className="mb-2" style={{ fontWeight: 600 }}>
          {title || 'Proyecto sin titulo'}
        </h5>

        {/* Client */}
        {client && (
          <p className="mb-2" style={{ fontSize: '0.9rem' }}>
            <i className="bi bi-building me-2"></i>
            {client.company_name || client.primary_contact_name || 'Cliente no especificado'}
          </p>
        )}

        {/* Date */}
        <div className="d-flex gap-3 mb-3" style={{ fontSize: '0.85rem' }}>
          <span>
            <i className="bi bi-calendar-event me-1"></i>
            {formatDate(created_at)}
          </span>
        </div>

        {/* Task Summary */}
        {taskSummary && (
          <div className="task-summary">
            <small className="d-block mb-2" style={{ color: '#6c757d' }}>
              <i className="bi bi-list-task me-1"></i>
              Tareas
            </small>
            <Row className="g-2 text-center">
              <Col xs={4}>
                <div
                  className="rounded py-2"
                  style={{
                    background: 'rgba(255, 193, 7, 0.1)',
                    border: '1px solid rgba(255, 193, 7, 0.3)',
                  }}
                >
                  <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#b38600' }}>
                    {taskSummary.pending || 0}
                  </div>
                  <small style={{ color: '#6c757d', fontSize: '0.7rem' }}>
                    Pendientes
                  </small>
                </div>
              </Col>
              <Col xs={4}>
                <div
                  className="rounded py-2"
                  style={{
                    background: 'rgba(13, 110, 253, 0.1)',
                    border: '1px solid rgba(13, 110, 253, 0.3)',
                  }}
                >
                  <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#0d6efd' }}>
                    {taskSummary.inProgress || 0}
                  </div>
                  <small style={{ color: '#6c757d', fontSize: '0.7rem' }}>
                    En Progreso
                  </small>
                </div>
              </Col>
              <Col xs={4}>
                <div
                  className="rounded py-2"
                  style={{
                    background: 'rgba(25, 135, 84, 0.1)',
                    border: '1px solid rgba(25, 135, 84, 0.3)',
                  }}
                >
                  <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#198754' }}>
                    {taskSummary.completed || 0}
                  </div>
                  <small style={{ color: '#6c757d', fontSize: '0.7rem' }}>
                    Completadas
                  </small>
                </div>
              </Col>
            </Row>
          </div>
        )}

        {/* Arrow indicator */}
        <div className="text-end mt-2">
          <i className="bi bi-chevron-right" style={{ color: '#adb5bd' }}></i>
        </div>
      </Card.Body>
    </Card>
  );
};

/**
 * Empty State Component
 */
const EmptyState = ({ hasFilters }) => (
  <Card className="dashboard-card">
    <Card.Body className="text-center py-5">
      <i
        className="bi bi-folder2-open display-1 mb-3"
        style={{ color: '#adb5bd' }}
      ></i>
      <h4>
        {hasFilters
          ? 'No se encontraron proyectos'
          : 'No tienes proyectos asignados'}
      </h4>
      <p className="text-muted mb-0">
        {hasFilters
          ? 'Intenta ajustar los filtros de busqueda'
          : 'Cuando se te asignen tareas en proyectos, apareceran aqui'}
      </p>
    </Card.Body>
  </Card>
);

/**
 * Loading State Component
 */
const LoadingState = () => (
  <div className="text-center py-5">
    <Spinner animation="border" role="status" variant="primary">
      <span className="visually-hidden">Cargando proyectos...</span>
    </Spinner>
    <p className="mt-3" style={{ color: '#6c757d' }}>
      Cargando proyectos...
    </p>
  </div>
);

/**
 * TechnicianProjects Main Component
 */
const TechnicianProjects = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Filter state
  const [statusFilter, setStatusFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Build query filters
  const queryFilters = useMemo(() => {
    const filters = {
      pageSize: 50,
    };

    if (statusFilter) {
      filters.status = statusFilter;
    }

    if (searchTerm.trim()) {
      filters.search = searchTerm.trim();
    }

    return filters;
  }, [statusFilter, searchTerm]);

  // Fetch projects
  const {
    data: projectsResponse,
    isLoading,
    isRefetching,
    refetch,
    isError,
    error,
  } = useQuery({
    queryKey: ['technician-projects', user?.id, queryFilters],
    // `getAssignedProjects` cierra con `catch { return { data:null, count:0,
    // error } }`: RESUELVE. Sin el unwrap el `error` de react-query es
    // estructuralmente siempre null, el `{error && <Alert>}` de abajo es
    // inalcanzable y el celular sin señal le dice al técnico "No tienes
    // proyectos asignados". Ver `src/utils/serviceResult.js`.
    queryFn: () => unwrapServiceResult(getAssignedProjects(user?.id, queryFilters)),
    enabled: !!user?.id,
    staleTime: CACHE_TIME.MEDIUM,
    refetchOnWindowFocus: true,
  });

  // Process projects data
  const allProjects = useMemo(() => {
    return projectsResponse?.data || [];
  }, [projectsResponse?.data]);

  // Extract unique clients from projects
  const uniqueClients = useMemo(() => {
    const clientMap = new Map();
    allProjects.forEach((p) => {
      if (p.client?.id) {
        clientMap.set(p.client.id, p.client.company_name || p.client.primary_contact_name || 'Sin nombre');
      }
    });
    return Array.from(clientMap.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allProjects]);

  // Filter projects by client
  const projects = useMemo(() => {
    if (!clientFilter) return allProjects;
    return allProjects.filter((p) => p.client?.id === clientFilter);
  }, [allProjects, clientFilter]);

  // Check if filters are active
  const hasFilters = useMemo(() => {
    return statusFilter !== '' || clientFilter !== '' || searchTerm.trim() !== '';
  }, [statusFilter, clientFilter, searchTerm]);

  // Handle pull-to-refresh
  const handleRefresh = useCallback(async () => {
    await refetch();
  }, [refetch]);

  // Handle project card click
  const handleProjectClick = useCallback(
    (projectId) => {
      navigate(`/tecnico/proyectos/${projectId}`);
    },
    [navigate]
  );

  // Handle status filter change
  const handleStatusChange = useCallback((e) => {
    setStatusFilter(e.target.value);
  }, []);

  // Handle search input change
  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  // Handle client filter change
  const handleClientChange = useCallback((e) => {
    setClientFilter(e.target.value);
  }, []);

  // Handle clear filters
  const handleClearFilters = useCallback(() => {
    setStatusFilter('');
    setClientFilter('');
    setSearchTerm('');
  }, []);

  return (
    <Container fluid className="technician-page p-0">
      {/* Page Header */}
      <div className="page-header px-3 pt-3 pb-2">
        <h1 className="page-title">Mis Proyectos</h1>
        <p className="page-subtitle">
          {isLoading
            ? 'Cargando...'
            : isError
            ? 'No se pudo leer tu lista de proyectos'
            : `${projects.length} proyecto${projects.length !== 1 ? 's' : ''} asignado${projects.length !== 1 ? 's' : ''}`}
        </p>
      </div>

      {/* Search Bar + Advanced Filters */}
      <div className="px-3 mb-3">
        <div className="search-bar-wrapper">
          <InputGroup>
            <InputGroup.Text style={{ background: '#f8f9fa', border: '1px solid #dee2e6', color: '#6c757d' }}>
              <i className="bi bi-search"></i>
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Buscar por numero o titulo..."
              value={searchTerm}
              onChange={handleSearchChange}
              aria-label="Buscar proyectos"
            />
            <button
              className={`btn ${statusFilter || clientFilter ? 'btn-primary' : 'btn-outline-secondary'} d-flex align-items-center gap-1`}
              onClick={() => setShowAdvanced(!showAdvanced)}
              aria-expanded={showAdvanced}
              type="button"
            >
              <i className={`bi bi-chevron-${showAdvanced ? 'up' : 'down'}`}></i>
              <span className="d-none d-sm-inline">Filtros</span>
              {(statusFilter || clientFilter) && (
                <Badge bg="light" text="dark" pill style={{ fontSize: '0.65rem' }}>
                  {(statusFilter ? 1 : 0) + (clientFilter ? 1 : 0)}
                </Badge>
              )}
            </button>
            {hasFilters ? (
              <button className="btn btn-outline-secondary" onClick={handleClearFilters} title="Limpiar filtros" type="button">
                <i className="bi bi-x-lg"></i>
              </button>
            ) : (
              <button className="btn btn-outline-secondary" onClick={handleRefresh} disabled={isRefetching} title="Actualizar" type="button">
                <i className={`bi bi-arrow-clockwise ${isRefetching ? 'spin' : ''}`}></i>
              </button>
            )}
          </InputGroup>
        </div>

        <Collapse in={showAdvanced}>
          <div>
            <Card className="dashboard-card mt-2">
              <Card.Body className="py-2 px-3">
                <Row className="g-2 align-items-center">
                  <Col xs={12} sm={6}>
                    <Form.Label className="small text-muted mb-1">Estado</Form.Label>
                    <Form.Select
                      size="sm"
                      value={statusFilter}
                      onChange={handleStatusChange}
                      aria-label="Filtrar por estado"
                    >
                      {STATUS_FILTER_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Form.Label className="small text-muted mb-1">Cliente</Form.Label>
                    <Form.Select
                      size="sm"
                      value={clientFilter}
                      onChange={handleClientChange}
                      aria-label="Filtrar por cliente"
                    >
                      <option value="">Todos los clientes</option>
                      {uniqueClients.map((client) => (
                        <option key={client.id} value={client.id}>
                          {client.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </div>
        </Collapse>
      </div>

      {/* Error Alert
          Sin `dismissible`: descartarlo dejaba la pantalla en el estado vacío
          mentiroso, que es exactamente lo que este bloque viene a evitar. */}
      {isError && (
        <div className="px-3">
          <Alert variant="danger">
            <Alert.Heading className="h6">
              <i className="bi bi-exclamation-triangle me-2"></i>
              Error al cargar proyectos
            </Alert.Heading>
            <p className="mb-2 small">{error?.message || 'Intente nuevamente'}</p>
            <Button variant="outline-danger" size="sm" onClick={() => refetch()}>
              <i className="bi bi-arrow-clockwise me-1" aria-hidden="true"></i>
              Reintentar
            </Button>
          </Alert>
        </div>
      )}

      {/* Projects List */}
      <div className="px-3">
        {isLoading ? (
          <LoadingState />
        ) : isError ? null : projects.length === 0 ? (
          <EmptyState hasFilters={hasFilters} />
        ) : (
          <>
            {/* Refreshing indicator */}
            {isRefetching && (
              <div className="text-center mb-2">
                <Spinner animation="border" size="sm" variant="primary" />
                <span className="ms-2" style={{ color: '#6c757d' }}>
                  Actualizando...
                </span>
              </div>
            )}

            {/* Project Cards */}
            {projects.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                onClick={() => handleProjectClick(project.id)}
              />
            ))}
          </>
        )}
      </div>

      {/* Pull to refresh hint (mobile) */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </Container>
  );
};

export default TechnicianProjects;
